import { useEvent } from '@fs/zion-frontend-friends'
import { useEffect, useState } from 'react'

const hasWindow = typeof window !== 'undefined'

const defaults = {
  /** The inner height of the window. This is vh instead of dvh because of Safari */
  '--layout-viewport-height': '100vh',
  /** The height of the visual viewport (see https://developer.mozilla.org/en-US/docs/Web/API/Visual_Viewport_API) */
  '--visual-viewport-height': 'var(--layout-viewport-height)',
  /** The top position of the visual viewport */
  '--visual-viewport-top': '0px',
  /** The inner height of the window minus the role-indicator height. Calculation done in @fs/zion-role-indicator */
  '--page-content-height': 'var(--layout-viewport-height)',
  /** The height of the role indicator. Set by @fs/zion-role-indicator */
  '--role-indicator-height': '0px',
  /** The size of the role indicator border. Set by @fs/zion-role-indicator */
  '--role-indicator-border': '0px',
  /** The height of the header from HF. Set by the @fs/zion-header */
  '--header-height': '0px',
  /** The height of the virtual keyboard. */
  '--virtual-keyboard-height': '0px',
}

/**
 * This hook sets CSS variables that are used for layout and positioning.
 * See https://developer.mozilla.org/en-US/docs/Web/API/Visual_Viewport_API for the
 * differences between layout viewport and visual viewport.
 * See also https://css-tricks.com/the-trick-to-viewport-units-on-mobile/ for the implementation inspiration
 *   --layout-viewport-height is used for the global page layout.
 *   --visual-viewport-height is used to keep ProgressOverlay always visible.
 *   --page-content-height is used to position overlays inside the role indicator.
 */
export default function useViewportVariables() {
  const [viewportVariables, setViewportVariables] = useState(defaults)
  const setViewportVariable = useEvent(
    (name, value) =>
      viewportVariables[name] !== value &&
      setViewportVariables((prevVariables) => ({ ...prevVariables, [name]: value }))
  )

  useEffect(() => {
    if (!hasWindow) return () => {}
    let visualViewportCleanup = () => {}

    // VisualViewport will account for the iOS on-screen keyboard or pinch-zooming changing the viewport. See https://github.com/WICG/visual-viewport and https://developer.mozilla.org/en-US/docs/Web/API/VisualViewport
    if (window.visualViewport) {
      const visualResizeHandler = () => {
        setViewportVariable('--visual-viewport-height', `${window.visualViewport.height}px`)
        setViewportVariable('--visual-viewport-top', `${window.visualViewport.pageTop}px`)
        setViewportVariable('--virtual-keyboard-height', `${getVirtualKeyboardHeight()}px`)
        setViewportVariable('--layout-viewport-height', `${getInnerHeight()}px`)
      }

      visualResizeHandler()

      window.visualViewport.addEventListener('resize', visualResizeHandler)
      window.visualViewport.addEventListener('scroll', visualResizeHandler)

      visualViewportCleanup = () => {
        window.visualViewport.removeEventListener('resize', visualResizeHandler)
        window.visualViewport.removeEventListener('scroll', visualResizeHandler)
      }
    }

    const windowResizeHandler = () => {
      // Update the layout-viewport-height
      setViewportVariable('--layout-viewport-height', `${getInnerHeight()}px`)
    }

    windowResizeHandler()
    window.addEventListener('resize', windowResizeHandler)

    return () => {
      visualViewportCleanup()
      window.removeEventListener('resize', windowResizeHandler)
    }
  }, [setViewportVariable])

  return viewportVariables
}

// Removing 0.1px from window.innerHeight prevents an extra scrollbar from appearing on Firefox (FRONTIER-1854)
function getInnerHeight() {
  return window.innerHeight - 0.1
}

/** Because opening the virtual keyboard affects the visualViewport, we can get the height of the virtual keyboard by subtracting the height of the visualViewport from the layout-viewport (i.e. window.innerHeight). However, we want only the change represented by the virtualKeyboard, not from pinch-zooming (or else the resulting value will be larger than expected when pinch-zoomed), and so we need to unscale the visualViewport height for this calculation */
function getVirtualKeyboardHeight() {
  const { height, scale } = window.visualViewport
  const innerHeight = getInnerHeight()
  const unscaledHeight = height * scale // scale represents the change to the visualViewport made by pinch-zooming
  return Math.max(0, innerHeight - unscaledHeight)
}
